import { MGPFallible, MGPOptional, MGPValidation, Utils } from '@everyboard/lib';
import { BooleanConfig } from '../../config/BooleanConfig';
import { EnumConfig } from '../../config/EnumConfig';
import { NumberConfig } from '../../config/NumberConfig';
import { RulesConfigDescription } from '../../config/RulesConfigDescription';
import { RulesConfigDescriptionLocalizable } from '../../config/RulesConfigDescriptionLocalizable';
import { GameNode } from '../../jscaip/AI/GameNode';
import { Coord, CoordFailure } from '../../jscaip/Coord';
import { DirectionFailure } from '../../jscaip/Direction';
import { GameStatus } from '../../jscaip/GameStatus';
import { Ordinal } from '../../jscaip/Ordinal';
import { Player, PlayerOrNone } from '../../jscaip/Player';
import { PlayerMap } from '../../jscaip/PlayerMap';
import { ConfigurableRules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { MGPValidators } from '../../utils/MGPValidator';
import { QuebecCastlesMove, QuebecCastlesTranslation } from './QuebecCastlesMove';
import { QuebecCastlesState } from './QuebecCastlesState';
export class QuebecCastlesFailure {
    static INVALID_INVADER_DISTANCE = (distance) => $localize `Move distance must be 2 for invader, not ${distance}`;
    static INVALID_DEFENDER_DISTANCE = (distance) => $localize `Move distance must be 1 for defender, not ${distance}`;
    static MUST_DROP_IN_YOUR_TERRITORY = () => $localize `You must drop in your own territory`;
    static CANNOT_DROP_IN_MOVE_PHASE = () => $localize `You cannot drop in move phase`;
    static CANNOT_MOVE_IN_DROP_PHASE = () => $localize `You cannot move in drop phase`;
    static MUST_DROP_ALL_YOUR_PIECES = () => $localize `You must drop all your pieces`;
    static MUST_DROP_ALL_YOUR_REMAINING_PIECES = () => $localize `You must drop all your remaining pieces, not more not less`;
    static CANNOT_DROP_THAT_MANY_PIECES = () => $localize `You cannot drop that many pieces`;
    static MUST_DROP_ONE_BY_ONE = () => $localize `You must drop pieces one by one`;
    static CANNOT_LAND_OR_DROP_IN_YOUR_CASTLE = () => $localize `You cannot land or drop on your castle`;
    static PLACE_ONLY_ONE_CASTLE = () => $localize `You must only place your castle`;
    static CANNOT_PUT_THAT_MANY_PIECE_IN_THERE_FOR_INVADER = (max, line) => $localize `If you have ${line} line(s), you can only have ${max} pieces (as invader)`;
    static CANNOT_PUT_THAT_MANY_PIECE_IN_THERE_FOR_DEFENDER = (max, line) => $localize `If you have ${line} line(s), you can only have ${max} pieces (as defender)`;
    static TOO_MANY_LINES_FOR_TERRITORY = () => $localize `Too many lines for territory, your opponent lines would merge with yours!`;
}
export const DropModes = {
    'AUTO': () => $localize `Automatic`,
    'PIECE_BY_PIECE': () => $localize `Piece by piece`,
    'BY_BATCH': () => $localize `By batch`,
};
export class QuebecCastlesNode extends GameNode {
}
export class QuebecCastlesRules extends ConfigurableRules {
    static singleton = MGPOptional.empty();
    static RULES_CONFIG_DESCRIPTION = new RulesConfigDescription({
        name: () => $localize `Quebec Castles`,
        config: {
            width: new NumberConfig(9, RulesConfigDescriptionLocalizable.WIDTH, MGPValidators.range(2, 20)),
            height: new NumberConfig(9, RulesConfigDescriptionLocalizable.HEIGHT, MGPValidators.range(2, 20)),
            linesForTerritory: new NumberConfig(5, () => $localize `Lines for territory`, MGPValidators.range(1, 10)),
            invaders: new NumberConfig(14, () => $localize `Number of invaders`, MGPValidators.range(1, 123456)),
            defenders: new NumberConfig(9, () => $localize `Number of defenders`, MGPValidators.range(1, 123456)),
            isRhombic: new BooleanConfig(true, () => $localize `Is Rhombic`),
            playersPlaceCastle: new BooleanConfig(false, () => $localize `Place castle yourself`),
            dropMode: new EnumConfig('AUTO', () => $localize `Drop mode`, DropModes),
        },
        validators: [
            QuebecCastlesRules.enoughLineForTerritory,
            QuebecCastlesRules.enoughPlaceForDefenders,
            QuebecCastlesRules.enoughPlaceForInvaders,
        ],
    });
    static enoughLineForTerritory(config) {
        let height;
        if (config.isRhombic) {
            height = config.width + config.height - 2;
        }
        else {
            height = config.height;
        }
        if (config.linesForTerritory < (height / 2)) {
            return MGPValidation.SUCCESS;
        }
        else {
            return MGPValidation.failure(QuebecCastlesFailure.TOO_MANY_LINES_FOR_TERRITORY());
        }
    }
    static enoughPlaceForInvaders(config) {
        return QuebecCastlesRules.isThereEnoughPlaceForPiece(Player.ZERO, config, config.invaders);
    }
    static enoughPlaceForDefenders(config) {
        return QuebecCastlesRules.isThereEnoughPlaceForPiece(Player.ONE, config, config.defenders);
    }
    static get() {
        if (QuebecCastlesRules.singleton.isAbsent()) {
            QuebecCastlesRules.singleton = MGPOptional.of(new QuebecCastlesRules());
        }
        return QuebecCastlesRules.singleton.get();
    }
    static isThereEnoughPlaceForPiece(player, config, numberOfPiece) {
        // Got to subtract 1 as the castle is not included
        const spaceForPiece = QuebecCastlesRules.get().getValidDropCoords(player, config).length - 1;
        if (spaceForPiece < numberOfPiece) {
            const line = config.linesForTerritory;
            const failure = player === Player.ZERO ?
                QuebecCastlesFailure.CANNOT_PUT_THAT_MANY_PIECE_IN_THERE_FOR_INVADER(spaceForPiece, line) :
                QuebecCastlesFailure.CANNOT_PUT_THAT_MANY_PIECE_IN_THERE_FOR_DEFENDER(spaceForPiece, line);
            return MGPValidation.failure(failure);
        }
        else {
            return MGPValidation.SUCCESS;
        }
    }
    getRulesConfigDescription() {
        return QuebecCastlesRules.RULES_CONFIG_DESCRIPTION;
    }
    getInitialState(config) {
        const castles = this.getCastles(config);
        const board = TableUtils.create(config.width, config.height, PlayerOrNone.NONE);
        let state = new QuebecCastlesState(board, 0, castles);
        if (config.dropMode === 'AUTO' && config.playersPlaceCastle === false) {
            state = this.fillBoard(state, config);
        }
        return state;
    }
    getCastles(config) {
        if (config.playersPlaceCastle) {
            const empty = MGPOptional.empty();
            return PlayerMap.ofValues(empty, empty);
        }
        else {
            const upperLeft = MGPOptional.of(new Coord(0, 0));
            const bottomRight = MGPOptional.of(new Coord(config.width - 1, config.height - 1));
            return PlayerMap.ofValues(bottomRight, upperLeft);
        }
    }
    fillBoard(state, config) {
        state = this.fillBoardFor(Player.ONE, state, config);
        state = this.fillBoardFor(Player.ZERO, state, config);
        return state;
    }
    fillBoardFor(player, state, config) {
        const initialCoords = this.getInitialCoords(player, state, config);
        for (const coord of initialCoords) {
            state = state.setPieceAt(coord, player);
        }
        return state;
    }
    getLineDirectionAndIndex(player, config) {
        const lineToFillRange = this.getLegalRangeIndex(player, config);
        if (player === Player.ZERO) {
            return { lineDirection: -1, lineToFillIndex: lineToFillRange.max };
        }
        else {
            return { lineDirection: 1, lineToFillIndex: lineToFillRange.min };
        }
    }
    getLineFirstCoord(line, pieceToDrop) {
        const availableSpaceEvenness = line.length % 2 === 0;
        const remainingSpace = line.length - pieceToDrop;
        const skipCenter = availableSpaceEvenness === false && (pieceToDrop % 2 === 0);
        const indexStart = Math.floor(remainingSpace / 2);
        const coord = line[indexStart]; // start on middle part that is on the right
        return { coord, skipCenter };
    }
    getInitialCoords(player, state, config) {
        let pieceToDrop = this.getNumberOfPieces(player, config);
        const coordDirection = config.isRhombic ? Ordinal.UP_RIGHT : Ordinal.RIGHT;
        const lineDirectionAndIndex = this.getLineDirectionAndIndex(player, config);
        const lineDirection = lineDirectionAndIndex.lineDirection;
        let lineToFillIndex = lineDirectionAndIndex.lineToFillIndex;
        const coords = [];
        while (pieceToDrop > 0) {
            const availableSpaceAtLine = this.getAvailableSpacesAtLine(lineToFillIndex, state, config);
            if (pieceToDrop < availableSpaceAtLine.length) {
                const firstLineCoord = this.getLineFirstCoord(availableSpaceAtLine, pieceToDrop);
                let coord = firstLineCoord.coord;
                const skipCenter = firstLineCoord.skipCenter;
                coords.push(coord);
                pieceToDrop--;
                const center = availableSpaceAtLine[Math.floor(availableSpaceAtLine.length / 2)];
                while (pieceToDrop > 0) {
                    coord = coord.getNext(coordDirection, 1);
                    if (skipCenter && coord.equals(center)) {
                        coord = coord.getNext(coordDirection, 1);
                    }
                    coords.push(coord);
                    pieceToDrop--;
                }
            }
            else {
                coords.push(...availableSpaceAtLine);
                pieceToDrop -= availableSpaceAtLine.length;
            }
            lineToFillIndex += lineDirection;
        }
        return coords;
    }
    getAvailableSpacesAtLine(line, state, config) {
        let defaultAvailableSpace;
        let coord;
        let direction;
        const coords = [];
        if (config.isRhombic) {
            const xMax = config.width - 1;
            const yMax = config.height - 1;
            const max = xMax + yMax;
            defaultAvailableSpace = Math.min(line + 1, max + 1 - line);
            const xInitial = Math.max(line - yMax, 0);
            const yInitial = Math.min(line, yMax);
            coord = new Coord(xInitial, yInitial);
            direction = Ordinal.UP_RIGHT;
        }
        else {
            defaultAvailableSpace = config.width;
            coord = new Coord(0, line);
            direction = Ordinal.RIGHT;
        }
        while (defaultAvailableSpace > 0) {
            if (state.isCastleAt(coord) === false) {
                coords.push(coord);
            }
            coord = coord.getNext(direction);
            defaultAvailableSpace--;
        }
        return coords;
    }
    isDropPhase(state, config) {
        return this.getExpectedDropsThisTurn(state, config) > 0;
    }
    isLegal(move, state, config) {
        if (this.isDropPhase(state, config)) {
            return this.isLegalDrop(move, state, config);
        }
        else {
            return this.isLegalTranslation(move, state);
        }
    }
    getExpectedDropsThisTurn(state, config) {
        switch (config.dropMode) {
            case 'PIECE_BY_PIECE':
                const totalPieceToDrop = config.defenders + config.invaders;
                if (state.turn < totalPieceToDrop) {
                    return this.getExpectedDropsThisTurnForPieceByPiece(state, config);
                }
                else {
                    return 0;
                }
            case 'BY_BATCH':
                return this.getExpectedDropsThisTurnForBatch(state, config);
            default:
                if (this.mustPlaceCastle(state, config)) {
                    return 1;
                }
                else {
                    return 0;
                }
        }
    }
    getExpectedDropsThisTurnForPieceByPiece(state, config) {
        if (this.mustPlaceCastle(state, config)) {
            return 1;
        }
        const totalPieceToDrop = config.defenders + config.invaders;
        const totalPieceDropped = state.countPieceOnBoard(Player.ZERO) + state.countPieceOnBoard(Player.ONE);
        if (totalPieceDropped === totalPieceToDrop) {
            return 0;
        }
        Utils.assert(state.turn < totalPieceToDrop, 'getExpectedDropsThisTurnForPieceByPiece should not be called after drop phase');
        // This turn is when the other player has dropped all its pieces
        // First player: 3; second player: 5 => turn 5 ==> drop 3
        // First player: 5; second player: 3 => turn 6 ==> drop 2
        let turnOfLastDrop = Math.min(config.defenders, config.invaders) * 2;
        if (config.playersPlaceCastle) {
            turnOfLastDrop += 2;
        }
        let dropBonus = 0;
        if (config.defenders < config.invaders) {
            turnOfLastDrop--;
            dropBonus = 1;
        }
        if (state.turn === turnOfLastDrop) {
            return Math.abs(config.defenders - config.invaders) + dropBonus;
        }
        else {
            return 1;
        }
    }
    getExpectedDropsThisTurnForBatch(state, config) {
        const toDrop = this.getNumberOfPieces(state.getCurrentPlayer(), config);
        if (state.turn < 2) {
            if (config.playersPlaceCastle) {
                return 1;
            }
            else {
                return toDrop;
            }
        }
        else if (state.turn < 4) {
            if (config.playersPlaceCastle) {
                return toDrop;
            }
        }
        return 0;
    }
    isLegalDrop(move, state, config) {
        if (QuebecCastlesMove.isTranslation(move)) {
            return MGPValidation.failure(QuebecCastlesFailure.CANNOT_MOVE_IN_DROP_PHASE());
        }
        if (this.mustPlaceCastle(state, config)) {
            return this.isLegalCastlePlacement(move, state, config);
        }
        else {
            return this.isLegalPieceDrop(move, state, config);
        }
    }
    mustPlaceCastle(state, config) {
        return state.turn < 2 && config.playersPlaceCastle;
    }
    isLegalCastlePlacement(move, state, config) {
        if (move.coords.size() === 1) {
            return this.getDropLegality(move.coords.getAnyElement().get(), state, config, false);
        }
        else {
            return MGPValidation.failure(QuebecCastlesFailure.PLACE_ONLY_ONE_CASTLE());
        }
    }
    isLegalPieceDrop(move, state, config) {
        if (config.dropMode === 'PIECE_BY_PIECE') {
            if (this.isLastDrop(state, config)) {
                const player = state.getCurrentPlayer();
                const playerCount = state.countPieceOnBoard(player);
                const playerTotal = this.getNumberOfPieces(player, config);
                const remainToDrop = playerTotal - playerCount;
                if (move.coords.size() !== remainToDrop) {
                    return MGPFallible.failure(QuebecCastlesFailure.MUST_DROP_ALL_YOUR_REMAINING_PIECES());
                }
            }
            else {
                if (move.coords.size() > 1) {
                    return MGPFallible.failure(QuebecCastlesFailure.MUST_DROP_ONE_BY_ONE());
                }
            }
        }
        else if (config.dropMode === 'BY_BATCH') {
            const numberToDrop = this.getNumberOfPieces(state.getCurrentPlayer(), config);
            if (move.coords.size() > numberToDrop) {
                return MGPFallible.failure(QuebecCastlesFailure.CANNOT_DROP_THAT_MANY_PIECES());
            }
            if (move.coords.size() < numberToDrop) {
                return MGPFallible.failure(QuebecCastlesFailure.MUST_DROP_ALL_YOUR_PIECES());
            }
        }
        for (const coord of move.coords) {
            const dropLegality = this.getDropLegality(coord, state, config, false);
            if (dropLegality.isFailure()) {
                return dropLegality;
            }
        }
        return MGPValidation.SUCCESS;
    }
    getNumberOfPieces(player, config) {
        return player === Player.ZERO ? config.defenders : config.invaders;
    }
    isLastDrop(state, config) {
        const opponent = state.getCurrentOpponent();
        const opponentCount = state.countPieceOnBoard(opponent);
        const opponentTotal = this.getNumberOfPieces(opponent, config);
        return opponentCount === opponentTotal; // If opponent dropped all its pieces (one by one)
        // Then you must now drop them all at once
    }
    getDropLegality(coord, state, config, isCastle) {
        if (state.isOnBoard(coord) === false) {
            return MGPValidation.failure(CoordFailure.OUT_OF_RANGE(coord));
        }
        const landingSquare = state.getPieceAt(coord);
        if (landingSquare.isPlayer()) {
            return MGPValidation.failure(RulesFailure.MUST_CLICK_ON_EMPTY_SPACE());
        }
        const player = state.getCurrentPlayer();
        if (state.castles.get(player).equalsValue(coord) && isCastle === false) {
            return MGPValidation.failure(QuebecCastlesFailure.CANNOT_LAND_OR_DROP_IN_YOUR_CASTLE());
        }
        if (this.isDropInPlayerTerritory(coord, player, config)) {
            return MGPValidation.SUCCESS;
        }
        else {
            return MGPValidation.failure(QuebecCastlesFailure.MUST_DROP_IN_YOUR_TERRITORY());
        }
    }
    getValidDropCoords(player, config) {
        const drops = [];
        for (let y = 0; y < config.height; y++) {
            for (let x = 0; x < config.width; x++) {
                const coord = new Coord(x, y);
                if (this.isDropInPlayerTerritory(coord, player, config)) {
                    drops.push(coord);
                }
            }
        }
        return drops;
    }
    isValidDrop(state, coord, player, config) {
        if (this.isDropInPlayerTerritory(coord, player, config)) {
            return state.getPieceAt(coord).isNone() && state.isCastleAt(coord) === false;
        }
        else {
            return false;
        }
    }
    isDropInPlayerTerritory(coord, player, config) {
        const y = coord.y;
        let metric = 0;
        if (config.isRhombic) {
            const x = coord.x;
            metric = x + y;
        }
        else {
            metric = y;
        }
        const minMax = this.getLegalRangeIndex(player, config);
        return minMax.min <= metric && metric <= minMax.max;
    }
    getLegalRangeIndex(player, config) {
        const yMax = config.height - 1;
        if (config.isRhombic) {
            const xMax = config.width - 1;
            const max = xMax + yMax;
            return this.getLegalRangeFromMaximum(player, config, max);
        }
        else {
            return this.getLegalRangeFromMaximum(player, config, yMax);
        }
    }
    getLegalRangeFromMaximum(player, config, max) {
        return {
            min: player === Player.ZERO ? (max + 1) - config.linesForTerritory : 0,
            max: player === Player.ZERO ? max : config.linesForTerritory - 1,
        };
    }
    isLegalTranslation(move, state) {
        if (QuebecCastlesMove.isDrop(move)) {
            return MGPValidation.failure(QuebecCastlesFailure.CANNOT_DROP_IN_MOVE_PHASE());
        }
        const startValidity = this.getStartValidity(state, move.getStart());
        if (startValidity.isFailure()) {
            return startValidity;
        }
        const endValidity = this.getLandingValidity(state, move.getEnd());
        if (endValidity.isFailure()) {
            return endValidity;
        }
        const middleValidity = this.getMiddleValidity(state, move);
        if (middleValidity.isFailure()) {
            return middleValidity;
        }
        return MGPValidation.SUCCESS;
    }
    getStartValidity(state, start) {
        if (state.isOnBoard(start) === false) {
            return MGPValidation.failure(CoordFailure.OUT_OF_RANGE(start));
        }
        const startPiece = state.getPieceAt(start);
        if (startPiece.isNone()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
        }
        if (startPiece === state.getCurrentOpponent()) {
            return MGPValidation.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
        }
        return MGPValidation.SUCCESS;
    }
    getLandingValidity(state, landing) {
        if (state.isOnBoard(landing) === false) {
            return MGPValidation.failure(CoordFailure.OUT_OF_RANGE(landing));
        }
        const landingSquare = state.getPieceAt(landing);
        const currentPlayer = state.getCurrentPlayer();
        if (landingSquare.isPlayer() && landingSquare.equals(currentPlayer)) {
            return MGPValidation.failure(RulesFailure.CANNOT_SELF_CAPTURE());
        }
        const playerCastle = state.castles.get(currentPlayer).get();
        if (landing.equals(playerCastle)) {
            return MGPValidation.failure(QuebecCastlesFailure.CANNOT_LAND_OR_DROP_IN_YOUR_CASTLE());
        }
        return MGPValidation.SUCCESS;
    }
    getMiddleValidity(state, move) {
        const direction = move.getDirection();
        if (direction.isFailure()) {
            return MGPValidation.failure(DirectionFailure.DIRECTION_MUST_BE_LINEAR());
        }
        const distance = move.getDistance();
        if (state.getCurrentPlayer() === Player.ZERO) {
            if (distance !== this.getPlayerStepSize(Player.ZERO)) {
                return MGPValidation.failure(QuebecCastlesFailure.INVALID_DEFENDER_DISTANCE(distance));
            }
        }
        else {
            if (distance !== this.getPlayerStepSize(Player.ONE)) {
                return MGPValidation.failure(QuebecCastlesFailure.INVALID_INVADER_DISTANCE(distance));
            }
            const middle = move.getJumpedOverCoords();
            const middlePiece = state.getPieceAt(middle[0]);
            if (middlePiece !== PlayerOrNone.NONE) {
                return MGPValidation.failure(RulesFailure.SOMETHING_IN_THE_WAY());
            }
        }
        return MGPValidation.SUCCESS;
    }
    applyLegalMove(move, state, config) {
        if (this.isDropPhase(state, config)) {
            return this.applyLegalDrop(move, state, config);
        }
        else {
            return this.applyLegalNormalMove(move, state);
        }
    }
    applyLegalDrop(move, state, config) {
        const currentPlayer = state.getCurrentPlayer();
        if (this.mustPlaceCastle(state, config)) {
            const castles = PlayerMap.ofValues(state.castles.get(Player.ZERO), state.castles.get(Player.ONE));
            const castleCoord = move.coords.getAnyElement().get();
            castles.put(currentPlayer, MGPOptional.of(castleCoord));
            if (config.dropMode === 'AUTO') {
                const adaptedDefaultConfig = {
                    ...this.getDefaultRulesConfig(),
                    width: config.width,
                    height: config.height,
                    isRhombic: config.isRhombic,
                };
                const newState = this.placeCastlesAndMovePiece(state, castles, adaptedDefaultConfig);
                return new QuebecCastlesState(newState.board, state.turn + 1, castles);
            }
            else {
                return new QuebecCastlesState(state.board, state.turn + 1, castles);
            }
        }
        else {
            let resultingState = state;
            for (const drop of move.coords) {
                resultingState = resultingState.setPieceAt(drop, currentPlayer);
            }
            return resultingState.incrementTurn();
        }
    }
    placeCastlesAndMovePiece(state, castles, config) {
        const initialState = this.getInitialState(config);
        let newState = this.doCastlePlacement(initialState, castles, Player.ZERO);
        if (state.getCurrentPlayer() === Player.ZERO) {
            initialState.forEachCoord((coord, content) => {
                if (content === Player.ONE) {
                    newState = newState.setPieceAt(coord, PlayerOrNone.NONE);
                }
            });
        }
        else {
            newState = this.doCastlePlacement(newState, castles, Player.ONE);
        }
        return newState;
    }
    doCastlePlacement(initialState, castles, player) {
        const actualCastle = castles.get(player).get();
        const defaultCastle = initialState.castles.get(player).get();
        if (initialState.getPieceAt(actualCastle).isPlayer()) {
            // The castle has been placed where a piece is on initial state
            // So we remove the "initial piece" where the actual castle is put
            initialState = initialState.setPieceAt(actualCastle, PlayerOrNone.NONE);
            // And move it to the place we know is by defaut empty: the default castle's spalce
            initialState = initialState.setPieceAt(defaultCastle, player);
        }
        return initialState;
    }
    applyLegalNormalMove(move, state) {
        const currentPlayer = state.getCurrentPlayer();
        return state
            .setPieceAt(move.getStart(), PlayerOrNone.NONE)
            .setPieceAt(move.getEnd(), currentPlayer)
            .incrementTurn();
    }
    getGameStatus(node, config) {
        const state = node.gameState;
        const defenderCastle = state.castles.get(Player.ONE);
        if (defenderCastle.isPresent() && state.getPieceAt(defenderCastle.get()).equals(PlayerOrNone.ZERO)) {
            return GameStatus.ZERO_WON; // Player.ZERO (Invader) stepped on Player.ONE (Defender)'s castle, victory
        }
        const invader = state.castles.get(Player.ZERO);
        if (invader.isPresent() && state.getPieceAt(invader.get()).equals(PlayerOrNone.ONE)) {
            return GameStatus.ONE_WON; // Player.ONE (Defender) stepped on Player.ZERO (Invader)'s castle, victory
        }
        const playerZeroPieces = state.countPieceOnBoard(Player.ZERO);
        if (this.isDropPhase(state, config) === false) {
            if (playerZeroPieces === 0) {
                return GameStatus.ONE_WON;
            }
            const playerOne = state.countPieceOnBoard(Player.ONE);
            if (playerOne === 0) {
                return GameStatus.ZERO_WON;
            }
        }
        return GameStatus.ONGOING;
    }
    getPlayerStepSize(player) {
        if (player === Player.ZERO) {
            return 1;
        }
        else {
            return 2;
        }
    }
    getPossibleMovesFor(coord, state) {
        const owner = state.getPieceAt(coord);
        const stepSize = this.getPlayerStepSize(owner);
        const moves = [];
        for (const direction of Ordinal.ORDINALS) {
            const step = coord.getNext(direction);
            if (state.isOnBoard(step)) {
                if (stepSize === 1) {
                    if (this.getLandingValidity(state, step).isSuccess()) {
                        moves.push(QuebecCastlesTranslation.of(coord, step));
                    }
                }
                else {
                    if (state.getPieceAt(step) === PlayerOrNone.NONE) {
                        const landing = coord.getNext(direction, 2);
                        if (this.getLandingValidity(state, landing).isSuccess()) {
                            moves.push(QuebecCastlesTranslation.of(coord, landing));
                        }
                    }
                }
            }
        }
        return moves;
    }
}
